import { useState, useMemo } from 'react';
import { useAllSitesForEdgeLink } from '@/lib/api/edgelink';
import { EdgeLinkSiteDto } from '@/lib/api/edgelink/types';
import { cn } from '@/lib/utils';
import { EMPTY_GUID } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Building2,
  X,
  AlertCircle,
  Check,
  ChevronsUpDown,
} from 'lucide-react';

interface SiteMultiSelectProps {
  value: string[];
  onChange: (siteIds: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export default function SiteMultiSelect({
  value,
  onChange,
  disabled,
  className,
}: SiteMultiSelectProps) {
  const { data: sites, isLoading, isError } = useAllSitesForEdgeLink();
  const [open, setOpen] = useState(false);

  // Group sites by company (filtering is handled by Command's built-in search)
  const groupedSites = useMemo(() => {
    if (!sites) return new Map<string, { companyName: string; sites: EdgeLinkSiteDto[] }>();

    const groups = new Map<string, { companyName: string; sites: EdgeLinkSiteDto[] }>();

    for (const site of sites) {
      // Skip archived sites
      if (site.archivedAt) continue;

      const existing = groups.get(site.companyId);
      if (existing) {
        existing.sites.push(site);
      } else {
        groups.set(site.companyId, {
          companyName: site.companyName,
          sites: [site],
        });
      }
    }

    return groups;
  }, [sites]);

  const selectedSites = useMemo(() => {
    if (!sites) return [];
    return sites.filter((s) => value.includes(s.id));
  }, [sites, value]);

  const handleToggleSite = (siteId: string) => {
    if (disabled) return;
    const newSelection = value.includes(siteId)
      ? value.filter((id) => id !== siteId)
      : [...value, siteId];
    onChange(newSelection);
    // DO NOT close popover - keep open for multi-select
  };

  const removeSite = (siteId: string) => {
    if (disabled) return;
    onChange(value.filter((id) => id !== siteId));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('flex items-center gap-2 text-destructive text-sm p-4 border border-destructive/30 rounded-lg', className)}>
        <AlertCircle className="h-4 w-4" />
        <span>Failed to load sites</span>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Selected Sites Pills - always render container to prevent layout shift */}
      <div className="flex flex-wrap gap-2 min-h-7 items-center">
        {selectedSites.length === 0 ? (
          <span className="text-xs text-muted-foreground">No sites selected</span>
        ) : (
          <>
            {selectedSites.map((site) => (
              <Badge
                key={site.id}
                variant="secondary"
                className="flex items-center gap-1.5 pr-1 py-1"
              >
                <Building2 className="h-3 w-3" />
                <span>{site.name}</span>
                <button
                  type="button"
                  onClick={() => removeSite(site.id)}
                  disabled={disabled}
                  className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {selectedSites.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAll}
                disabled={disabled}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
              >
                Clear all
              </Button>
            )}
          </>
        )}
      </div>

      {/* Site Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span>
                {value.length === 0
                  ? 'Select sites to assign...'
                  : `${value.length} site${value.length !== 1 ? 's' : ''} selected`}
              </span>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search sites or companies..." />
            <CommandList>
              <CommandEmpty>
                <div className="py-6 text-center">
                  <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No sites found</p>
                </div>
              </CommandEmpty>

              {Array.from(groupedSites.entries()).map(([companyId, { companyName, sites: companySites }]) => (
                <CommandGroup key={companyId} heading={companyName}>
                  {companySites.map((site) => {
                    const isSelected = value.includes(site.id);
                    const hasExistingRoute = site.defaultRouteNamespaceId &&
                      site.defaultRouteNamespaceId !== EMPTY_GUID;

                    return (
                      <CommandItem
                        key={site.id}
                        value={`${site.name} ${companyName} ${site.address1 || ''}`}
                        onSelect={() => handleToggleSite(site.id)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Building2
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            isSelected ? 'text-lime' : 'text-muted-foreground'
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'text-sm font-medium truncate',
                            isSelected && 'text-lime'
                          )}>
                            {site.name}
                          </p>
                          {site.address1 && (
                            <p className="text-xs text-muted-foreground truncate">
                              {site.address1}
                            </p>
                          )}
                        </div>
                        {hasExistingRoute && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-yellow-500/30 text-yellow-500 shrink-0 ml-2"
                          >
                            Has Route
                          </Badge>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Help text */}
      <p className="text-xs text-muted-foreground">
        Selected sites will have their default EdgeLink route set to this bridge's first route after creation.
      </p>
    </div>
  );
}
